import { object, text } from '../workflows/repository';
import type { Row, Value } from '../workflows/types';
import { joinedReference, isOpaque } from '../workflows/reference.presentation';

export function warehouseRows(value: Value | undefined): Row[] {
  return Array.isArray(value) ? value.map(object) : [];
}

export function warehouseLink(view: string, context: Record<string, string> = {}): string {
  const query = new URLSearchParams({ view });
  for (const [key, value] of Object.entries(context)) if (value) query.set(key, value);
  return `/admin/warehouse?${query}`;
}

/** Keep server decimal strings intact; absence is never a quantity of zero. */
export function quantity(
  value: Value | undefined,
  unit: Value | undefined,
  absent = 'Not reported'
): string {
  return value === null || value === undefined || value === ''
    ? absent
    : `${text(value)}${text(unit) ? ` ${text(unit)}` : ''}`;
}

const explanations: Record<string, string> = {
  PACK_UNIT_REQUIRED: 'Pack unit is missing',
  UNKNOWN_PACK_UNIT: 'Pack unit is not recognised',
  UNKNOWN_WAREHOUSE_PRODUCT_CODE:
    'This warehouse product code is not linked. Review product-link proposals and select the matching application product.',
  AMBIGUOUS_WAREHOUSE_PRODUCT_CODE:
    'This warehouse product code matches more than one product. Review the product links.',
  PRODUCT_CODE_CONFLICT:
    'The source product references disagree. Review the original row and product links.',
  NO_BASE_UNIT: 'The product has no base unit configured. Review its unit settings.',
  NO_CONVERSION_FACTOR:
    'A required unit conversion is missing. Review the product packaging and effective conversion dates.',
  MALFORMED_ROW: 'The source row does not match the report columns. Review the original file.',
  UNKNOWN_TRANSACTION_TYPE:
    'The transaction type is not recognized. Review the original warehouse row.',
  UNPARSEABLE_QUANTITY: 'The quantity could not be read. Review its value in the original file.',
  UNPARSEABLE_DATE: 'A date could not be read. Review its value in the original file.',
  UNPARSEABLE_LOT: 'The lot could not be read. Review its format and lot mapping.',
  QUANTITY_NOT_WHOLE:
    'The reported quantity must be a whole number for this unit. Review the source quantity.',
  DUPLICATE_TRANSACTION:
    'This transaction was already recorded. Review the retained original before treating it as new stock.',
  DUPLICATE_SERIAL: 'This serial number was already recorded. Review the retained original.',
  DATABASE_CONSTRAINT:
    'The row could not be saved. Ask an administrator to review the technical evidence.',

  awaiting_receipt:
    'The warehouse reports stock whose arrival still needs confirmation. Review the receipt before changing stock.',
  awaiting_receipt_confirmation: 'Arrival needs confirmation',
  agreed: 'The reported and expected quantities agree.',
  discrepancy:
    'A difference needs a recorded decision. The warehouse quantity has not been adopted.',
  agree: 'The two stock sources agree.',
  differs: 'The stock sources differ. Review their dates and lot evidence.',
  portal_only:
    'The portal reports this lot; no warehouse position was returned. Its warehouse quantity is unknown.',
  warehouse_only:
    'The warehouse reports this lot; no portal stock was returned. Its portal quantity is unknown.',
  report_missing: 'The daily report had not arrived by its configured deadline.',
  configuration_missing:
    'The report check could not run with its current settings. Ask an administrator to review setup.',
  WAITING_FOR_REMAINING_LOTS: 'Some shipment lots have not been received yet.',
  QUANTITY_DIFFERS:
    'Received and shipped quantities differ. Review the pallet quantities before confirming.',
  AUTO_CONFIRM_OFF: 'Automatic confirmation is off. A person must review this exact match.',
  SEVERAL_CANDIDATES:
    'More than one shipment could match. Choose the correct shipment after reviewing the evidence.',
  LOT_NOT_KNOWN:
    'A warehouse lot is not linked to an application lot. Review the lot difference first.',
  NO_CANDIDATE:
    'No eligible shipment matches these goods. Review the shipment route, lots and quantities.',
  PREREQUISITE_MISSING:
    'A shipment prerequisite is missing. Review its milestones and required evidence.',
  NOT_EVALUATED: 'Matching has not been evaluated yet. Use Retry matching to check it.',
  incomplete_input: 'Some source rows were rejected; this quantity is incomplete.',
  gap: 'Daily reports are missing between the compared dates.',
  no_master_expiry: 'The application lot has no authoritative expiry date.',
  RECEIPT_SOURCE_SWITCH_DATE_REQUIRED:
    'The receipt source switch date needs configuration before reconciliation can run.',
  OPENING_POSITION_NOT_ADOPTED:
    'Review a daily report and adopt the warehouse opening stock first.',
  RECONCILIATION_OUT_OF_ORDER: 'Reconcile the earlier daily reports before this one.',
};

export function warehouseExplanation(value: Value | undefined): string {
  const code = text(value);
  return explanations[code] ?? (code ? code.replaceAll('_', ' ') : 'No reason recorded.');
}

export function pickupWarnings(movement: Row): {
  older: number;
  unchecked: number;
  unmatched: number;
} {
  const lines = warehouseRows(movement.lines);
  return {
    older: lines.filter((line) => object(line.fifo_check).status === 'older_lot_available').length,
    unchecked: lines.filter((line) => fifoPresentation(line.fifo_check).state === 'not_evaluated')
      .length,
    unmatched: lines.filter((line) => !warehouseRows(line.inbound).length).length,
  };
}

export function warehouseCompletionMessage(actionId: string, result: Value): string {
  if (actionId === 'upload')
    return object(result).already_held === true
      ? 'This file was already held. No rows were reprocessed. Review the retained report and its existing rejection evidence.'
      : 'Report retained. Review rejected rows, stock reconciliation and receipt matches before relying on its balances.';
  return 'Decision recorded. Review the refreshed record and any returned warnings.';
}

/** Counts only the loaded records; never infer a successful outcome from an absent status. */
export function warehouseQueueSummary(
  view: string,
  rows: Row[]
): { review: number; recorded: number; unknown: number } {
  let review = 0,
    recorded = 0;
  for (const row of rows) {
    if (view === 'reports') {
      const rejected = warehouseCount(row.lines_rejected);
      if (rejected !== null && rejected > 0) review++;
      else if (rejected === 0) recorded++;
    } else {
      const status = text(row.status);
      const states: Record<string, [string[], string[]]> = {
        receipts: [
          ['proposed', 'ambiguous', 'unmatched', 'blocked'],
          ['confirmed', 'auto_confirmed', 'dismissed'],
        ],
        discrepancies: [['open'], ['resolved']],
        movements: [['picked_up'], ['delivered']],
        alerts: [['raised', 'acknowledged'], ['cleared']],
        inbox: [['raised', 'acknowledged'], ['cleared']],
      };
      if (view === 'expected') review++;
      else if (states[view]?.[0].includes(status)) review++;
      else if (states[view]?.[1].includes(status)) recorded++;
    }
  }
  return { review, recorded, unknown: rows.length - review - recorded };
}

export function warehouseCount(value: Value | undefined): number | null {
  if (typeof value !== 'number' && typeof value !== 'string') return null;
  if (typeof value === 'string' && !value.trim()) return null;
  const count = Number(value);
  return Number.isSafeInteger(count) && count >= 0 ? count : null;
}

/** Describe only the evidence returned; missing collections are not empty collections. */
export function receiptEvidenceSummary(data: Row) {
  const lines = warehouseRows(data.lines);
  const sources = new Set<string>();
  const first = text(data.first_seen_report_id);
  if (first) sources.add(first);
  for (const line of lines) {
    const source = text(line.report_id);
    if (source) sources.add(source);
  }
  return {
    lines,
    rowCount: Array.isArray(data.lines) ? lines.length : null,
    sourceIds: [...sources],
    missingSourceRows: lines.filter((line) => !text(line.report_id)).length,
  };
}

/** Search business labels and source row numbers, never internal reference IDs. */
export function warehouseGoodsMatch(line: Row, query: string, resolvedName = ''): boolean {
  const terms = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const searchable = [
    joinedReference('product_id', line),
    joinedReference('batch_lot_id', line),
    resolvedName,
    line.product_code,
    line.product_name,
    line.lot_reported,
    line.lot_normalised,
    line.lot_code,
    line.serial_number,
    line.row_number,
    ...warehouseRows(line.inbound).flatMap((ship) => [
      ship.shipment_number,
      ...(Array.isArray(ship.asn_numbers) ? ship.asn_numbers : []),
    ]),
  ]
    .map((value) => text(value))
    .join(' ')
    .toLowerCase();
  return terms.every((term) => searchable.includes(term));
}

/** A saved FIFO result is advisory. Unknown results never imply a successful check. */
export function fifoPresentation(value: Value | undefined): {
  state: 'older_lot_available' | 'in_order' | 'not_evaluated';
  label: string;
  explanation: string;
  tone: 'warning' | 'success' | 'muted';
} {
  const check = object(value);
  if (check.status === 'older_lot_available')
    return {
      state: 'older_lot_available',
      label: 'Older usable stock was available',
      tone: 'warning',
      explanation:
        'The recorded pickup used a newer lot. Review the older stock below; this warning does not block delivery or shipment closure.',
    };
  if (check.status === 'in_order')
    return {
      state: 'in_order',
      label: 'FIFO check passed',
      tone: 'success',
      explanation: 'No older usable lot was found in the stock available that day.',
    };
  const reasons: Record<string, string> = {
    NO_STOCK_REPORT: 'No stock report was available for the pickup date.',
    PICKED_LOT_UNDATED: 'The picked lot could not be dated from its code.',
  };
  return {
    state: 'not_evaluated',
    label:
      text(check.status) && check.status !== 'not_evaluated'
        ? 'FIFO result unavailable'
        : 'FIFO not evaluated',
    tone: 'muted',
    explanation:
      reasons[text(check.reason)] ??
      (text(check.status)
        ? 'An evaluated FIFO result is unavailable. Review the retained row evidence.'
        : 'No FIFO check was recorded for this pickup.'),
  };
}

export type MovementShipmentLink = {
  key: string;
  id: string;
  names: string[];
  asns: string[];
  rows: number[];
};

/** Deduplicate repeated links by their returned shipment identity, never by name or ASN. */
export function movementShipmentLinks(lines: Row[]): MovementShipmentLink[] {
  const links = new Map<string, MovementShipmentLink>();
  lines.forEach((line, row) =>
    warehouseRows(line.inbound).forEach((ship, index) => {
      const id = text(ship.shipment_id),
        key = id || `unidentified-${row}-${index}`;
      const current = links.get(key) ?? { key, id, names: [], asns: [], rows: [] };
      const name = text(ship.shipment_number);
      if (name && !isOpaque(name) && !current.names.includes(name)) current.names.push(name);
      for (const value of Array.isArray(ship.asn_numbers) ? ship.asn_numbers : []) {
        const asn = text(value);
        if (asn && !isOpaque(asn) && !current.asns.includes(asn)) current.asns.push(asn);
      }
      if (!current.rows.includes(row + 1)) current.rows.push(row + 1);
      links.set(key, current);
    })
  );
  return [...links.values()];
}

export function movementFifoReviewReasons(lines: Row[]): { explanation: string; count: number }[] {
  const counts = new Map<string, number>();
  for (const line of lines) {
    const result = fifoPresentation(line.fifo_check);
    if (result.state === 'not_evaluated')
      counts.set(result.explanation, (counts.get(result.explanation) ?? 0) + 1);
  }
  return [...counts].map(([explanation, count]) => ({ explanation, count }));
}

export function shipmentReference(value: Value | undefined): string {
  const reference = text(value);
  return !reference || reference === 'NONE' ? '—' : reference;
}

/** Returned rows retain the source CSV columns in raw. */
export function warehouseRejectionExplanation(row: Row): string {
  const unit = text(object(row.raw).PACKSHORT_DESC);
  return row.reason_code === 'UNKNOWN_PACK_UNIT' && unit
    ? `Pack unit '${unit}' is not recognised`
    : warehouseExplanation(row.reason_code);
}
